import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Typography, Select } from "antd";
import { useTranslation } from "react-i18next";
import axios, { AxiosError, AxiosResponse } from "axios";
import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";
import { IUser } from "../../Users";
import { IRequirement } from "../../Project";
import filterOptions from "../../../utils/filterOptions";

const { Text } = Typography;

interface IOption {
  label: string;
  value: string;
}

interface IAssignment {
  identifier: string;
  description: string;
  requirement_id: string;
  consultant_id?: string;
}

interface IAssignActivityModalProps {
  activity?: IRequirement;
  visible: boolean;
  handleClose: (activity?: IRequirement) => void;
}

const AssignActivityModal: React.FC<IAssignActivityModalProps> = ({
  activity,
  visible,
  handleClose,
}) => {
  const { t } = useTranslation(["activities-actions"]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assignedUser, setAssignedUser] = useState<string>();
  const [users, setUsers] = useState<IUser[]>([]);

  const { addToast } = useToast();

  useEffect(() => {
    setAssignedUser(activity?.consultant_id);
  }, [activity?.consultant_id]);

  useEffect(() => {
    setLoadingUsers(true);
    api
      .get<IUser[]>("/users")
      .then(response => {
        setUsers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os consultores, tente novamente.",
        });
      })
      .finally(() => setLoadingUsers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usersOptions = useMemo(() => {
    if (!users) {
      return undefined;
    }

    return users
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(user => ({
        label: user.name,
        value: user.id,
      }));
  }, [users]);

  const handleSelectUser = useCallback((consultant_id: string) => {
    setAssignedUser(consultant_id);
  }, []);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 405:
          description = "O consultor não foi encontrado";
          break;
        case 404:
          description = "A atividade não foi encontrada";
          break;
        case 403:
          description =
            "A atividade só pode ser reatribuída pelo usuário atribuído ou por um gestor";
          break;
        default:
          description =
            "Um erro occoreu ao atribuir a atividade, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro ao atribuir",
        description,
      });
    },
    [addToast],
  );

  const handleSubmit = useCallback(async () => {
    setLoading(true);

    try {
      const response = await api.patch(`/activities/${activity?.id}/assign`, {
        consultant_id: assignedUser,
      });

      handleClose(response.data);

      addToast({
        type: "success",
        title: "Atividade atribuída",
        description: "A atividade foi atribuída com sucesso",
      });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const serverError = err as AxiosError;
        generateErrorToast(serverError.response);
      } else {
        console.log(err);
      }
    }

    setLoading(false);
  }, [activity?.id, addToast, assignedUser, generateErrorToast, handleClose]);

  return (
    <Modal
      title={t("assign")}
      visible={visible}
      onOk={handleSubmit}
      destroyOnClose
      onCancel={() => handleClose()}
      okButtonProps={{
        loading,
      }}
      getContainer={false}
      bodyStyle={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Text style={{ marginBottom: 10 }}>
        {`Selecione o usuário para o qual atribuir a atividade "${activity?.description}"`}
      </Text>
      <Select
        showSearch
        onChange={handleSelectUser}
        defaultValue={activity?.consultant_id}
        style={{ width: 200 }}
        options={usersOptions}
        loading={loadingUsers}
        filterOption={filterOptions}
      />
    </Modal>
  );
};

export default AssignActivityModal;
